import { useCallback, useState } from 'react';
import { batch } from 'react-redux';

import * as St from './StoresList.styles';

import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useStoresContext } from '~/legacy/easy-estimator-page/context/storesContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { updateIsNewEstimate } from '~/redux/slices/estimate';
import { selectZipcode, updateZipcode } from '~/redux/slices/zipcode';
import LandingPageImage from '~atoms/LandingPageImage';
import RetailStores from '~organisms/RetailStores';
import RetailStoresMap from '~organisms/RetailStoresMap';
import StoreChangeModal from '~organisms/StoreChangeModal';
import ZipNotServicedModal from '~organisms/ZipNotServicedModal';
import StoreSearch from '~templates/StoreSearch';

type StoresListProps = {
  showZipCodeInput?: boolean;
  showStoresList?: boolean;
  disabled?: boolean;
  isZipDisabled?: boolean;
};

const StoresList = ({
  showZipCodeInput = true,
  showStoresList = true,
  disabled = false,
  isZipDisabled = false,
}: StoresListProps) => {
  const dispatch = useAppDispatch();
  const zipCode = useAppSelector(selectZipcode);
  const [isZipModalVisible, setIsZipModalVisible] = useState(false);
  const [newZipCode, setNewZipCode] = useState<string>();
  const [isZipNotServicedVisible, setIsZipNotServicedVisible] = useState(false);
  const [zipNotServicedZipCode, setZipNotServicedZipCode] = useState<string>();
  const [zipResetSignal, setZipResetSignal] = useState(0);

  const { getAlerts, replaceProductsWithEquivalents } =
    useEquivalentProductMatching();

  const {
    getters: { googleMapsApiKey, googleMapsMapId },
  } = useStoresContext();

  const closeZipModal = useCallback(() => {
    setIsZipModalVisible(false);
  }, []);

  const updateZipCodeAndReplaceProducts = useCallback(async () => {
    if (newZipCode) {
      const alerts = await getAlerts({ zipCode: newZipCode });

      if (alerts.length > 0) {
        setZipNotServicedZipCode(newZipCode);
        setIsZipNotServicedVisible(true);
        setZipResetSignal((s) => s + 1);
        closeZipModal();

        return;
      }

      const result = await replaceProductsWithEquivalents({
        zipCode: newZipCode,
      });

      if (!result) {
        setZipNotServicedZipCode(newZipCode);
        setIsZipNotServicedVisible(true);
        setZipResetSignal((s) => s + 1);
        closeZipModal();

        return;
      }

      dispatch(updateZipcode(newZipCode));
    }

    closeZipModal();
  }, [
    closeZipModal,
    dispatch,
    getAlerts,
    newZipCode,
    replaceProductsWithEquivalents,
  ]);

  const showMap = Boolean(googleMapsApiKey) && Boolean(googleMapsMapId);

  return (
    <St.Container>
      <StoreSearch
        showZipCodeInput={showZipCodeInput}
        disabled={disabled}
        isZipDisabled={isZipDisabled}
        resetSignal={zipResetSignal}
        onSubmitWithNonEmptyDrawingArea={(newZipcode) => {
          setNewZipCode(newZipcode);
          setIsZipModalVisible(true);
        }}
        onSubmitWithEmptyDrawingArea={async (newZipcode) => {
          const alerts = await getAlerts({ zipCode: newZipcode });

          if (alerts.length > 0) {
            setZipNotServicedZipCode(newZipcode);
            setIsZipNotServicedVisible(true);
            setZipResetSignal((s) => s + 1);

            return;
          }

          const result = await replaceProductsWithEquivalents({
            zipCode: newZipcode,
          });

          if (!result) {
            setZipNotServicedZipCode(newZipcode);
            setIsZipNotServicedVisible(true);
            setZipResetSignal((s) => s + 1);
          }

          batch(() => {
            dispatch(updateZipcode(newZipcode));
            dispatch(updateIsNewEstimate(true));
          });
        }}
      />
      {showStoresList && !zipCode && <LandingPageImage />}
      {showStoresList && <RetailStores disabled={disabled} />}
      {showStoresList && showMap && zipCode && (
        <St.MapContainer>
          <RetailStoresMap disabled={disabled} />
        </St.MapContainer>
      )}
      <ZipNotServicedModal
        zipCode={zipNotServicedZipCode}
        isVisible={isZipNotServicedVisible}
        onOk={() => {
          setIsZipNotServicedVisible(false);
          setZipNotServicedZipCode(undefined);
        }}
      />
      <StoreChangeModal
        newZipCode={newZipCode}
        isVisible={isZipModalVisible}
        handleClose={closeZipModal}
        handleChange={updateZipCodeAndReplaceProducts}
      />
    </St.Container>
  );
};

export default StoresList;
